import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, IconButton, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ClearIcon from '@mui/icons-material/Clear';
import { Controller, useForm } from 'react-hook-form';
import {
  getCharacterCountHelperText,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
} from '../../../../utils';
import { textLimits } from '../../../../core/constants/constants';

interface CreateMigrationRecordFormType {
  title: string;
  description: string;
  member_ids: string[];
}

type AddRecordProps = {
  closeModal: () => void;
  onAddRecord: (payload: any) => void;
  treeId: string;
  userId?: string | null;
  treeMembers?: any[];
};

export default function CreateMigrationRecord({
  closeModal,
  onAddRecord,
  treeId,
  userId,
  treeMembers = [],
}: AddRecordProps) {
  const { control, handleSubmit, setValue } =
    useForm<CreateMigrationRecordFormType>({
      defaultValues: {
        title: '',
        description: '',
        member_ids: [],
      },
    });

  const onSubmit = async (data: CreateMigrationRecordFormType) => {
    if (!userId) return;

    const payload = {
      title: sanitiseNameInput(data.title),
      description: sanitiseTextInput(data.description),
      tree_id: treeId,
      member_ids: data.member_ids ?? [],
      created_by: userId,
    };
    if (onAddRecord) {
      onAddRecord(payload);
    }
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Create migration record
        </Typography>
        <IconButton onClick={closeModal}>
          <ClearIcon />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add a migration journey and capture key moments in your family&apos;s
        movement across places.
      </Typography>
      <Box
        py={1}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          p: 2.5,
          animation: 'recordCreateFade 220ms cubic-bezier(0.2, 0, 0, 1)',
          '@keyframes recordCreateFade': {
            '0%': { opacity: 0, transform: 'translateY(5px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <form>
          <Controller
            name="title"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Title"
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                placeholder="e.g. From Lagos to London"
                required
                inputProps={{ maxLength: textLimits.migrationTitle }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                InputLabelProps={{ shrink: true }}
                multiline
                rows={4}
                fullWidth
                margin="normal"
                placeholder="Share the reason, timeline, and impact of this migration journey"
                required
                inputProps={{ maxLength: textLimits.migrationDescription }}
                error={hasReachedCharacterLimit(
                  field.value,
                  textLimits.migrationDescription,
                )}
                helperText={getCharacterCountHelperText(
                  field.value,
                  textLimits.migrationDescription,
                )}
              />
            )}
          />
          <Controller
            name="member_ids"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={treeMembers}
                getOptionLabel={(member) =>
                  [member.first_name, member.middle_name, member.last_name]
                    .filter(Boolean)
                    .join(' ')
                }
                value={treeMembers.filter((member) =>
                  (field.value ?? []).includes(member.id),
                )}
                onChange={(_, newValue) =>
                  setValue(
                    'member_ids',
                    newValue.map((member) => member.id),
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Related family members"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Select people involved"
                    fullWidth
                    margin="normal"
                    helperText="Choose family members this journey relates to"
                  />
                )}
              />
            )}
          />
        </form>
      </Box>
      <Box
        py={2}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          alignItems: 'center',
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          onClick={closeModal}
          sx={{ borderRadius: 2, minHeight: 40, px: 2, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            minHeight: 40,
            px: 2,
            textTransform: 'none',
            transition:
              'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: 3,
            },
          }}
        >
          Create journey
        </Button>
      </Box>
    </Box>
  );
}
